import React, { useState, useRef, useEffect, useCallback } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Alert } from '../../../components/ui/alert';
import { useAuthStore } from '../../../stores/authStore';
import { TESTCHAT_WS_URL } from '../../../config/constants';

interface TestChatProps {
  domainId: string;
  kbSyncStatus?: string | null;
}

interface Message {
  role: 'user' | 'assistant';
  content: string;
  sources?: Array<{ file_name: string; similarity: number }>;
  isStreaming?: boolean;
}

const WATCHDOG_MS = 45_000;

export const TestChat: React.FC<TestChatProps> = ({ domainId, kbSyncStatus }) => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const wsRef = useRef<WebSocket | null>(null);
  const currentMessageIdRef = useRef<string | null>(null);
  const watchdogRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const bottomRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (messages.length === 0) return;
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // unmount cleanup
  useEffect(() => {
    return () => {
      if (watchdogRef.current) clearTimeout(watchdogRef.current);
      if (wsRef.current) wsRef.current.close();
    };
  }, []);

  const clearWatchdog = () => {
    if (watchdogRef.current) {
      clearTimeout(watchdogRef.current);
      watchdogRef.current = null;
    }
  };

  const handleSend = useCallback(() => {
    const content = input.trim();
    if (!content || isSending) return;

    const idToken = useAuthStore.getState().idToken;
    if (!idToken || !TESTCHAT_WS_URL || TESTCHAT_WS_URL === 'WSS_URL_PLACEHOLDER') {
      setError('接続設定が未完了です');
      return;
    }

    setInput('');
    setError(null);
    setMessages(prev => [...prev, { role: 'user', content }]);
    setIsSending(true);

    const ws = new WebSocket(`${TESTCHAT_WS_URL}?Authorization=${encodeURIComponent(idToken)}`);
    wsRef.current = ws;

    // watchdog: 45秒以内にchat_end/error/oncloseが来なければ強制終了
    watchdogRef.current = setTimeout(() => {
      ws.close();
      setError('応答がありませんでした');
      setIsSending(false);
    }, WATCHDOG_MS);

    ws.onopen = () => {
      ws.send(JSON.stringify({ type: 'message', message: content, domain_id: domainId }));
    };

    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);

        if (data.type === 'chat_response') {
          currentMessageIdRef.current = data.message_id;
          setMessages(prev => [...prev, { role: 'assistant', content: '', isStreaming: true }]);
        } else if (data.type === 'chat_chunk') {
          if (data.message_id === currentMessageIdRef.current) {
            setMessages(prev => {
              const updated = [...prev];
              const last = updated[updated.length - 1];
              if (last?.isStreaming) {
                updated[updated.length - 1] = { ...last, content: last.content + (data.content ?? '') };
              }
              return updated;
            });
          }
        } else if (data.type === 'chat_end') {
          clearWatchdog();
          setMessages(prev => {
            const updated = [...prev];
            const last = updated[updated.length - 1];
            if (last?.isStreaming) {
              updated[updated.length - 1] = { ...last, isStreaming: false, sources: data.sources ?? [] };
            }
            return updated;
          });
          currentMessageIdRef.current = null;
          ws.close();
        } else if (data.type === 'fallback') {
          clearWatchdog();
          setMessages(prev => [...prev, {
            role: 'assistant',
            content: data.message || '',
            isStreaming: false
          }]);
          ws.close();
        } else if (data.type === 'error') {
          clearWatchdog();
          setError(data.message || 'エラーが発生しました');
          ws.close();
        }
      } catch {
        // ignore malformed messages
      }
    };

    ws.onerror = () => {
      clearWatchdog();
      setError('接続エラーが発生しました');
      ws.close();
    };

    ws.onclose = () => {
      clearWatchdog();
      setIsSending(false);
      wsRef.current = null;
    };
  }, [input, isSending, domainId]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const showTypingIndicator = isSending && (messages.length === 0 || messages[messages.length - 1].role === 'user');

  return (
    <div className="flex flex-col" style={{ height: '500px' }}>
      {kbSyncStatus && kbSyncStatus !== 'synced' && kbSyncStatus !== 'completed' && (
        <Alert variant="warning" className="mb-3 flex-shrink-0" data-testid="test-chat-kb-guidance">
          学習データを同期してからテストチャットをお試しください
        </Alert>
      )}

      {/* チャットウィンドウ */}
      <div className="flex-1 flex flex-col min-h-0 rounded-xl border border-gray-200 overflow-hidden shadow-sm">
        {/* ヘッダー */}
        <div className="flex items-center gap-2 px-4 py-3 flex-shrink-0" style={{ backgroundColor: '#0a7a72' }}>
          <div className="w-7 h-7 rounded-full bg-white bg-opacity-20 flex items-center justify-center">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
            </svg>
          </div>
          <span className="text-white text-sm font-medium">チャットボットをテスト</span>
        </div>

        {/* メッセージ一覧 */}
        <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-gray-50" data-testid="test-chat-messages">
          {messages.length === 0 && !showTypingIndicator && (
            <p className="text-sm text-gray-400 text-center mt-6">お客様になったつもりで質問してみてください</p>
          )}
          {messages.map((msg, i) => (
            <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
              <div
                className={`max-w-[78%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${
                  msg.role === 'user'
                    ? 'text-white whitespace-pre-wrap'
                    : 'bg-white border border-gray-200 text-gray-800'
                }`}
                style={msg.role === 'user' ? { backgroundColor: '#0a7a72' } : undefined}
                data-testid={`test-chat-message-${msg.role}`}
              >
                {msg.role === 'user' ? (
                  <>{msg.content}{msg.isStreaming && <span className="animate-pulse ml-0.5">▌</span>}</>
                ) : (
                  <>
                    <ReactMarkdown
                      remarkPlugins={[remarkGfm]}
                      components={{
                        p: ({ children }) => <p className="mb-1 last:mb-0">{children}</p>,
                        ul: ({ children }) => <ul className="list-disc pl-4 mb-1">{children}</ul>,
                        ol: ({ children }) => <ol className="list-decimal pl-4 mb-1">{children}</ol>,
                        li: ({ children }) => <li className="mb-0.5">{children}</li>,
                        strong: ({ children }) => <strong className="font-semibold">{children}</strong>,
                        h1: ({ children }) => <h1 className="font-bold text-sm mt-1 mb-0.5">{children}</h1>,
                        h2: ({ children }) => <h2 className="font-bold text-sm mt-1 mb-0.5">{children}</h2>,
                        h3: ({ children }) => <h3 className="font-semibold text-sm mt-1 mb-0.5">{children}</h3>,
                        code: ({ children }) => <code className="bg-gray-100 rounded px-0.5 text-xs font-mono">{children}</code>,
                        pre: ({ children }) => <pre className="bg-gray-100 rounded p-2 text-xs overflow-auto my-1">{children}</pre>,
                        table: ({ children }) => <table className="border-collapse text-xs my-1 w-full">{children}</table>,
                        th: ({ children }) => <th className="border border-gray-300 px-1.5 py-0.5 bg-gray-100 font-semibold">{children}</th>,
                        td: ({ children }) => <td className="border border-gray-300 px-1.5 py-0.5">{children}</td>,
                      }}
                    >
                      {msg.content}
                    </ReactMarkdown>
                    {msg.isStreaming && <span className="animate-pulse">▌</span>}
                  </>
                )}
              </div>
            </div>
          ))}
          {showTypingIndicator && (
            <div className="flex justify-start">
              <div className="bg-white border border-gray-200 rounded-2xl px-4 py-2.5 text-sm text-gray-500" data-testid="test-chat-loading">
                <span className="animate-pulse">応答中…</span>
              </div>
            </div>
          )}
          <div ref={bottomRef} />
        </div>

        {/* エラー */}
        {error && (
          <div className="px-4 py-1.5 bg-red-50 border-t border-red-100 flex-shrink-0">
            <p className="text-xs text-red-500">{error}</p>
          </div>
        )}

        {/* 入力エリア */}
        <div className="flex items-end gap-2 px-4 py-3 bg-white border-t border-gray-200 flex-shrink-0">
          <textarea
            className="flex-1 border border-gray-200 rounded-xl px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:border-transparent"
            style={{ '--tw-ring-color': '#0a7a72' } as React.CSSProperties}
            rows={1}
            placeholder="質問を入力してみましょう…"
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            disabled={isSending}
            data-testid="test-chat-input"
          />
          <button
            onClick={handleSend}
            disabled={!input.trim() || isSending}
            className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 transition-opacity disabled:opacity-40"
            style={{ backgroundColor: '#0a7a72' }}
            data-testid="test-chat-send-button"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <line x1="22" y1="2" x2="11" y2="13" />
              <polygon points="22 2 15 22 11 13 2 9 22 2" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
};

export default TestChat;
